import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { UserProfileDto } from '../models/user-profile.model';
import { LocalStorageService } from '../services/local-storage.service';
import { UserProfileService } from '../services/user-profile.service';
import { UserAdminDialogComponent, UserAdminDialogData } from './user-admin-dialog/user-admin-dialog.component';
import { TeacherDialogComponent, TeacherDialogData } from '../teachers/teacher-dialog/teacher-dialog.component';

function passwordsMatchValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const np = group.get('newPassword')?.value;
    const cp = group.get('confirmPassword')?.value;
    if (!np && !cp) {
      return null;
    }
    return np === cp ? null : { mismatch: true };
  };
}

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css']
})
export class ProfileComponent implements OnInit {

  isAdmin = false;
  loadingMe = false;
  savingProfile = false;
  savingPassword = false;
  loadMeError: string | null = null;

  profileForm: FormGroup;
  passwordForm: FormGroup;
  hideNew = true;
  hideConfirm = true;
  hideCurrent = true;

  userDirectory = new MatTableDataSource<UserProfileDto>([]);
  teacherDirectory = new MatTableDataSource<UserProfileDto>([]);
  directoryColumns = ['username', 'name', 'email', 'phone', 'roles', 'actions'];
  loadingDirectory = false;
  tenantId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private userProfileService: UserProfileService,
    private localStorage: LocalStorageService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.isAdmin = this.localStorage.isAdmin();
    this.tenantId = this.localStorage.getTenantId();

    this.profileForm = this.fb.group({
      username: [{ value: '', disabled: true }],
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: ['', [Validators.email]],
      phoneNumber: ['']
    });

    this.passwordForm = this.fb.group({
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', Validators.required]
    }, { validators: passwordsMatchValidator() });

    this.loadMyProfile();
    if (this.isAdmin) {
      this.loadUserDirectory();
    }
  }

  loadMyProfile(): void {
    this.loadingMe = true;
    this.loadMeError = null;
    this.userProfileService.getMyProfile().subscribe(
      u => {
        this.applyProfileToForm(u);
        this.mergeStoredUser(u);
        this.loadingMe = false;
      },
      () => {
        this.loadingMe = false;
        this.loadMeError = 'Could not load profile. Ensure GET /api/v1/users/me exists.';
        this.prefillFromLocalStorage();
      }
    );
  }

  private prefillFromLocalStorage(): void {
    const u = this.localStorage.getUser();
    if (u && typeof u === 'object') {
      this.profileForm.patchValue({
        username: u.username || '',
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        email: u.email || '',
        phoneNumber: u.phoneNumber || u.mobileNumber || ''
      });
    }
  }

  private applyProfileToForm(u: UserProfileDto): void {
    this.profileForm.patchValue({
      username: u.username || '',
      firstName: u.firstName || '',
      lastName: u.lastName || '',
      email: u.email || '',
      phoneNumber: u.phoneNumber || u.mobileNumber || ''
    });
  }

  private mergeStoredUser(u: UserProfileDto): void {
    const prev = this.localStorage.getUser() || {};
    this.localStorage.saveUser({
      ...prev,
      username: u.username ?? prev.username,
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      phoneNumber: u.phoneNumber ?? u.mobileNumber,
      id: u.id ?? prev.id
    });
  }

  saveProfile(): void {
    if (this.profileForm.invalid) {
      return;
    }
    const v = this.profileForm.getRawValue();
    this.savingProfile = true;
    this.userProfileService
      .updateMyProfile({
        firstName: v.firstName.trim(),
        lastName: v.lastName.trim(),
        email: (v.email || '').trim() || undefined,
        phoneNumber: (v.phoneNumber || '').trim() || undefined
      })
      .subscribe(
        u => {
          this.savingProfile = false;
          this.applyProfileToForm(u);
          this.mergeStoredUser(u);
          this.snackBar.open('Profile updated', 'Dismiss', { duration: 3000 });
        },
        () => {
          this.savingProfile = false;
          this.snackBar.open('Could not save profile.', 'Dismiss', { duration: 5000 });
        }
      );
  }

  changePassword(): void {
    if (this.passwordForm.invalid) {
      return;
    }
    const v = this.passwordForm.value;
    this.savingPassword = true;
    this.userProfileService
      .changeMyPassword({
        currentPassword: v.currentPassword,
        newPassword: v.newPassword
      })
      .subscribe(
        () => {
          this.savingPassword = false;
          this.passwordForm.reset();
          Object.keys(this.passwordForm.controls).forEach(k => this.passwordForm.get(k)?.setErrors(null));
          this.passwordForm.setErrors(null);
          this.snackBar.open('Password changed', 'Dismiss', { duration: 3000 });
        },
        () => {
          this.savingPassword = false;
          this.snackBar.open('Could not change password. Check current password.', 'Dismiss', { duration: 5000 });
        }
      );
  }

  loadUserDirectory(): void {
    this.loadingDirectory = true;
    this.userProfileService.listUsers().subscribe(
      list => {
        const users = list || [];
        this.userDirectory.data = users;
        this.teacherDirectory.data = users.filter(u => this.userHasTeacherRole(u));
        this.loadingDirectory = false;
      },
      () => {
        this.userDirectory.data = [];
        this.teacherDirectory.data = [];
        this.loadingDirectory = false;
        this.snackBar.open('Could not load user list (admin).', 'Dismiss', { duration: 4000 });
      }
    );
  }

  private userHasTeacherRole(u: UserProfileDto): boolean {
    return (u.roles || []).includes('ROLE_TEACHER');
  }

  openAddTeacherFromProfile(): void {
    this.openProvisionDialog(false);
  }

  openProvisionUserFromProfile(): void {
    this.openProvisionDialog(true);
  }

  private openProvisionDialog(allowRoleChoice: boolean): void {
    if (this.tenantId == null) {
      this.snackBar.open('Missing school (tenant) id. Log in again after the API returns tenant or JWT claim.', 'Dismiss', { duration: 5000 });
      return;
    }
    const data: TeacherDialogData = { tenantId: this.tenantId, allowRoleChoice };
    this.dialog
      .open(TeacherDialogComponent, { width: '520px', maxHeight: '90vh', data })
      .afterClosed()
      .subscribe(saved => {
        if (saved) {
          const msg = allowRoleChoice ? 'Staff user created.' : 'Teacher account created.';
          this.snackBar.open(msg, 'Dismiss', { duration: 4000 });
          this.loadUserDirectory();
        }
      });
  }

  openEditUser(row: UserProfileDto): void {
    if (!row.id) {
      return;
    }
    const data: UserAdminDialogData = { user: { ...row } };
    this.dialog
      .open(UserAdminDialogComponent, { width: '480px', data })
      .afterClosed()
      .subscribe(saved => {
        if (saved) {
          this.snackBar.open('User updated', 'Dismiss', { duration: 3000 });
          this.loadUserDirectory();
        }
      });
  }

  rolesText(row: UserProfileDto): string {
    return (row.roles || []).join(', ') || '—';
  }
}
